import { useQuery } from '@tanstack/react-query';
import {
  ChartBarSquareIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  MinusIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { hmdaQualityApi } from '../lib/api';
import { useTeamId } from '../hooks/useTeamId';
import FeatureGate from '../components/FeatureGate';
import clsx from 'clsx';

const EDIT_TYPE_CONFIG: Record<string, { label: string; color: string }> = {
  syntactical: { label: 'Syntactical', color: 'bg-red-500' },
  validity: { label: 'Validity', color: 'bg-orange-500' },
  quality: { label: 'Quality', color: 'bg-yellow-500' },
  macro: { label: 'Macro', color: 'bg-blue-500' },
};

const SEVERITY_BADGE: Record<string, string> = {
  HIGH: 'bg-red-100 text-red-700',
  MEDIUM: 'bg-yellow-100 text-yellow-700',
  LOW: 'bg-green-100 text-green-700',
};

export default function HmdaQualityDashboardPage() {
  // teamId is part of every key below so each tenant gets its own cache entry;
  // a team switch can never surface another tenant's quality data from cache.
  const { teamId } = useTeamId();

  // Dashboard
  const { data: dashData, isLoading, isError, refetch } = useQuery({
    queryKey: ['hmdaQualityDashboard', teamId],
    queryFn: () => hmdaQualityApi.getDashboard(),
    enabled: !!teamId,
  });
  const dash = dashData?.data;

  // Edit failures
  const { data: editData } = useQuery({
    queryKey: ['hmdaQualityEditFailures', teamId],
    queryFn: () => hmdaQualityApi.getEditFailures(),
    enabled: !!teamId,
  });
  const edits = editData?.data;

  // LO error rates
  const { data: loData } = useQuery({
    queryKey: ['hmdaQualityLOErrors', teamId],
    queryFn: () => hmdaQualityApi.getLOErrorRates(),
    enabled: !!teamId,
  });

  // Trends
  const { data: trendsData } = useQuery({
    queryKey: ['hmdaQualityTrends', teamId],
    queryFn: () => hmdaQualityApi.getTrends({ months: 12 }),
    enabled: !!teamId,
  });

  // Systemic errors
  const { data: systemicData } = useQuery({
    queryKey: ['hmdaQualitySystemic', teamId],
    queryFn: () => hmdaQualityApi.getSystemicErrors(),
    enabled: !!teamId,
  });

  const TrendIcon = dash?.trendDirection === 'IMPROVING' ? ArrowTrendingUpIcon
    : dash?.trendDirection === 'DECLINING' ? ArrowTrendingDownIcon : MinusIcon;
  const trendColor = dash?.trendDirection === 'IMPROVING' ? 'text-green-600'
    : dash?.trendDirection === 'DECLINING' ? 'text-red-600' : 'text-gray-500';

  const scoreColor = (score: number) =>
    score > 80 ? 'text-green-600' : score > 60 ? 'text-yellow-600' : 'text-red-600';

  return (
    <FeatureGate feature="hmda_quality_monitoring" mode="lock">
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Data Quality</h1>
          <p className="mt-1 text-sm text-gray-500">
            Track edit failure rates, identify systemic errors, and monitor data quality over time
          </p>
        </div>

        {isError ? (
          <div className="bg-white rounded-lg shadow-sm border border-red-200 p-8 text-center">
            <p className="text-red-600 font-medium mb-2">Failed to load data</p>
            <p className="text-sm text-gray-500 mb-4">Please check your connection and try again.</p>
            <button onClick={() => refetch()} className="px-4 py-2 bg-teal-600 text-white rounded-md hover:bg-teal-700 text-sm">
              Retry
            </button>
          </div>
        ) : isLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-blue-600" />
          </div>
        ) : !dash ? (
          <div className="rounded-lg border bg-white py-12 text-center text-gray-500">
            <ChartBarSquareIcon className="mx-auto h-12 w-12 text-gray-300" />
            <p className="mt-4 text-lg font-medium">No HMDA data yet</p>
            <p className="mt-1 text-sm">Upload and validate LAR files to see quality metrics.</p>
          </div>
        ) : (
          <>
            {/* Summary cards — when there are no validations, all four metrics are
                meaningless. Show "No data" instead of "Score 0 + Trend STABLE",
                which reads as a contradiction in QA. */}
            {(() => {
              const hasData = (dash.totalValidations ?? 0) > 0;
              return (
                <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
                  <div className="rounded-lg border bg-white p-4">
                    <p className="text-xs font-medium text-gray-500">Quality Score</p>
                    {hasData ? (
                      <p className={clsx('mt-1 text-2xl font-bold', scoreColor(dash.overallQualityScore))}>
                        {dash.overallQualityScore}
                      </p>
                    ) : (
                      <p className="mt-1 text-2xl font-bold text-gray-300">—</p>
                    )}
                  </div>
                  <div className="rounded-lg border bg-white p-4">
                    <p className="text-xs font-medium text-gray-500">Total Validations</p>
                    <p className={clsx(
                      'mt-1 text-2xl font-bold',
                      hasData ? 'text-gray-900' : 'text-gray-300'
                    )}>
                      {dash.totalValidations}
                    </p>
                  </div>
                  <div className="rounded-lg border bg-white p-4">
                    <p className="text-xs font-medium text-gray-500">Clean Loan %</p>
                    {hasData ? (
                      <p className="mt-1 text-2xl font-bold text-gray-900">{dash.cleanLoanPercentage}%</p>
                    ) : (
                      <p className="mt-1 text-2xl font-bold text-gray-300">—</p>
                    )}
                  </div>
                  <div className="rounded-lg border bg-white p-4">
                    <p className="text-xs font-medium text-gray-500">Trend</p>
                    {hasData ? (
                      <div className="mt-1 flex items-center gap-1">
                        <TrendIcon className={clsx('h-6 w-6', trendColor)} />
                        <span className={clsx('text-sm font-medium', trendColor)}>{dash.trendDirection}</span>
                      </div>
                    ) : (
                      <p className="mt-1 text-sm font-medium text-gray-400">No data yet</p>
                    )}
                  </div>
                </div>
              );
            })()}

            {/* Edit Failure Breakdown */}
            {edits && (
              <div className="rounded-lg border bg-white">
                <div className="border-b px-4 py-3">
                  <h3 className="text-sm font-semibold text-gray-900">Edit Failure Breakdown</h3>
                </div>
                <div className="p-4">
                  {/* Bars */}
                  <div className="space-y-3">
                    {Object.entries(EDIT_TYPE_CONFIG).map(([key, { label, color }]) => {
                      const count = edits.summary?.[key] || 0;
                      const total = Object.values(edits.summary || {}).reduce((s: number, v: any) => s + (v || 0), 0);
                      const pct = total > 0 ? (count / total) * 100 : 0;
                      // Use raw pct (no Math.max floor) so bars with 0 count render
                      // empty — the old min-1% width painted color even on zero rows.
                      return (
                        <div key={key} className="flex items-center gap-3">
                          <span className="w-20 text-xs font-medium text-gray-500">{label}</span>
                          <div className="flex-1 h-5 rounded-full bg-gray-100 overflow-hidden">
                            {count > 0 && (
                              <div className={clsx('h-full rounded-full', color)} style={{ width: `${pct}%` }} />
                            )}
                          </div>
                          <span className="w-12 text-right text-xs font-medium text-gray-700">{count}</span>
                        </div>
                      );
                    })}
                  </div>

                  {/* Top failing edits */}
                  {edits.topFailingEdits?.length > 0 && (
                    <div className="mt-4">
                      <p className="text-xs font-medium text-gray-500 mb-2">Top Failing Edits</p>
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Code</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Description</th>
                            <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">Count</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {edits.topFailingEdits.slice(0, 10).map((e: any) => (
                            <tr key={e.editCode}>
                              <td className="px-3 py-2 text-sm font-mono font-medium text-gray-900">{e.editCode}</td>
                              <td className="px-3 py-2 text-sm text-gray-500 truncate max-w-[300px]">{e.description}</td>
                              <td className="px-3 py-2 text-right text-sm font-medium text-gray-900">{e.count}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* LO Error Rate Rankings */}
            {loData?.data && loData.data.length > 0 && (
              <div className="rounded-lg border bg-white">
                <div className="border-b px-4 py-3">
                  <h3 className="text-sm font-semibold text-gray-900">Loan Officer Error Rate Rankings</h3>
                  <p className="mt-0.5 text-xs text-gray-500">% of loans with at least one edit failure</p>
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">LO ID</th>
                        <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">Total Loans</th>
                        <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">Loans w/ Errors</th>
                        <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">Total Errors</th>
                        <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">Error Rate</th>
                        <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Top Errors</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {loData.data.map((lo: any) => {
                        const rateColor = lo.errorRate > 10 ? 'text-red-600' : lo.errorRate > 5 ? 'text-yellow-600' : 'text-green-600';
                        return (
                          <tr key={lo.loanOfficerId} className="hover:bg-gray-50">
                            <td className="whitespace-nowrap px-4 py-3 text-sm font-mono text-gray-900">{lo.loanOfficerId}</td>
                            <td className="whitespace-nowrap px-4 py-3 text-sm text-right text-gray-500 tabular-nums">{lo.totalLoans}</td>
                            <td className="whitespace-nowrap px-4 py-3 text-sm text-right text-gray-500 tabular-nums">{lo.loansWithErrors ?? '—'}</td>
                            <td className="whitespace-nowrap px-4 py-3 text-sm text-right text-gray-500 tabular-nums">{lo.totalErrors}</td>
                            <td className={clsx('whitespace-nowrap px-4 py-3 text-sm font-medium text-right tabular-nums', rateColor)}>
                              {lo.errorRate.toFixed(1)}%
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-500">
                              {(lo.topErrors || []).slice(0, 3).map((e: any) => {
                                const code = e.code || e.editCode;
                                return (
                                  <span key={code} className="mr-1 inline-flex rounded bg-gray-100 px-1.5 py-0.5 text-xs font-mono">
                                    {code}: {e.count}
                                  </span>
                                );
                              })}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Quality Trends */}
            {trendsData?.data && trendsData.data.length > 0 && (
              <div className="rounded-lg border bg-white">
                <div className="border-b px-4 py-3">
                  <h3 className="text-sm font-semibold text-gray-900">Quality Trend</h3>
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Month</th>
                        <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Validations</th>
                        <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Total Loans</th>
                        <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Clean %</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {trendsData.data.map((t: any) => (
                        <tr key={t.month} className="hover:bg-gray-50">
                          <td className="whitespace-nowrap px-4 py-3 text-sm font-medium text-gray-900">{t.month}</td>
                          <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-500">{t.validations}</td>
                          <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-500">{t.totalLoans}</td>
                          {(() => {
                            // Backend returns `cleanLoanPct`; older shape used `cleanPercentage`
                            const pct = t.cleanLoanPct ?? t.cleanPercentage;
                            const num = typeof pct === 'number' ? pct : Number(pct);
                            const display = Number.isFinite(num) ? `${num.toFixed(1)}%` : '—';
                            return (
                              <td className={clsx('whitespace-nowrap px-4 py-3 text-sm font-medium', scoreColor(Number.isFinite(num) ? num : 0))}>
                                {display}
                              </td>
                            );
                          })()}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Systemic Errors */}
            {systemicData?.data && systemicData.data.length > 0 && (
              <div className="rounded-lg border bg-white">
                <div className="border-b px-4 py-3">
                  <h3 className="text-sm font-semibold text-gray-900">Systemic Error Detection</h3>
                </div>
                <div className="divide-y divide-gray-200">
                  {systemicData.data.map((err: any) => (
                    <div key={err.editCode} className="flex items-start gap-3 p-4">
                      <ExclamationTriangleIcon className={clsx('mt-0.5 h-5 w-5 flex-shrink-0',
                        err.severity === 'HIGH' ? 'text-red-500' : err.severity === 'MEDIUM' ? 'text-yellow-500' : 'text-gray-400'
                      )} />
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-mono font-medium text-gray-900">{err.editCode}</span>
                          <span className={clsx('inline-flex rounded-full px-2 py-0.5 text-xs font-medium', SEVERITY_BADGE[err.severity] || SEVERITY_BADGE.LOW)}>
                            {err.severity}
                          </span>
                          <span className="text-xs text-gray-400">
                            {err.totalOccurrences ?? err.occurrenceCount ?? 0} occurrences across {err.validationsAffected ?? err.affectedValidations ?? 0} validations
                          </span>
                        </div>
                        <p className="mt-0.5 text-sm text-gray-500">{err.fieldName}: {err.description}</p>
                        {err.suggestedAction && (
                          <p className="mt-1 text-xs text-blue-600">{err.suggestedAction}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </FeatureGate>
  );
}
